import { useEffect, useRef } from "react";
import { useQuery } from "react-query";
import http from "../../api/core/api";
import { apiUrl } from "../../api/core/apiUrl";
import { localStorageKeys } from "../../utils/keys/localStorageKeys";

interface Props {
  path: string;
  fileName: string;
  attachmentHeight?: number;
}

const FILE_CACHE_MS = 15 * 60 * 1000;

const authHeaders = () => {
  const token = localStorage.getItem(localStorageKeys.BEARER_TOKEN_KEY);
  if (token) {
    return { Authorization: `Bearer ${JSON.parse(token)}` };
  }
  return null;
};

export function BoardContentCardFilePreview({
  path,
  fileName,
  attachmentHeight,
}: Props) {
  const imagePlaceholder = `/img/placeholder-image.png`;
  const imageRef = useRef<HTMLImageElement>(null);

  const { data: file } = useQuery<Blob | null>(
    ["network-file", path],
    async () => {
      const response = await http.apiGetFiles({
        url: `${apiUrl.BOARD_CONTENT_NETWORK_FILE}?path=${encodeURIComponent(path)}`,
        config: {
          headers: { ...authHeaders(), "Content-Type": "application/json" },
          method: "GET",
          responseType: "blob",
        },
      });
      return (response?.data as Blob) ?? null;
    },
    {
      enabled: !!path,
      staleTime: FILE_CACHE_MS,
    }
  );

  useEffect(() => {
    if (!file || !imageRef.current) return;
    const objectUrl = URL.createObjectURL(file);
    imageRef.current.src = objectUrl;
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  if (!file) return <>Loading</>;

  return (
    <div style={{ textAlign: "center" }}>
      <img
        ref={imageRef}
        src={imagePlaceholder}
        className="figure-img img-fluid rounded"
        style={{
          width: "auto",
          minHeight: attachmentHeight ? undefined : "80vh",
          maxHeight: attachmentHeight ? attachmentHeight + "px" : undefined,
          marginBottom: 0,
        }}
        alt={fileName}
        title={fileName}
      />
    </div>
  );
}
